import { gql, useMutation, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'

import {
  ItemResaleRaffleUserDetailFragmentDoc,
  PaymentDetailFragmentDoc,
  PurchaseResaleItemMutation,
  PurchaseResaleItemMutationVariables,
  ResaleRaffleRepaymentConfirmationQuery,
  ResaleRaffleRepaymentConfirmationQueryVariables,
} from '@/clients/utoniq-core/schema'
import { constants } from '@/config'
import { useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { setItemSku } from '@/core/sku'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages, generatePath } =
    useRouter<{ itemId: string; raffleId: string }>()
  const { raffleId, itemId } = router.query
  const { showError } = useMessage()
  const { data, loading } = useQuery<
    ResaleRaffleRepaymentConfirmationQuery,
    ResaleRaffleRepaymentConfirmationQueryVariables
  >(QUERY, {
    variables: {
      raffleId,
      itemId,
    },
  })
  const [openError, setOpenError] = useState<boolean>(false)

  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  const [purchaseResaleItem, { loading: submitting }] = useMutation<
    PurchaseResaleItemMutation,
    PurchaseResaleItemMutationVariables
  >(PURCHASE_RESALE_ITEM)

  const itemResaleRaffleUser = useMemo(() => {
    if (!data?.itemResaleRaffleUser) {
      return null
    }
    return {
      ...data?.itemResaleRaffleUser,
      item: setItemSku(
        data?.itemResaleRaffleUser?.item,
        data?.itemResaleRaffleUser.skuId
      ),
    }
  }, [data?.itemResaleRaffleUser])

  return {
    state: {
      loading,
      openError,
      submitting,
      itemResaleRaffleUser,
      brand: data?.paymentSource?.brand ?? '',
      cardNumber: data?.paymentSource?.last4,
      openExpiredCardModal,
      openPaymentErrorModal,
      openRequire3DSecureModal,
    },
    actions: {
      onPurchase: async () => {
        return purchaseResaleItem({
          variables: {
            inputs: {
              resaleRaffleId: raffleId,
              itemId,
            },
          },
        })
          .then(() => {
            router.push(
              generatePath(
                generatePath(routerPages.ResaleRaffleRepaymentCompletion, {
                  raffleId,
                  itemId,
                })
              )
            )
          })
          .catch(e => {
            switch (e.graphQLErrors?.[0]?.extensions?.errorCode) {
              /**
               * @description
               * 3Dセキュア認証が必要な場合
               */
              case constants.errorCodes.REQUIRE_3D_SECURE:
                setOpenRequire3DSecureModal(true)
                return
              /**
               * @desciption
               * カードが有効期限切れの場合
               */
              case constants.errorCodes.EXPIRED_CARD:
                setOpenExpiredCardModal(true)
                return
              /**
               * @desciption
               * 何かしらの決済エラーの場合
               */
              case constants.errorCodes.PAYMENT_ERROR:
                setOpenPaymentErrorModal(true)
                return
              default:
                showError(e)
                break
            }
          })
      },
      onCloseError: () => {
        setOpenError(false)
      },
      onCloseRequire3DSecureModal: () => {
        setOpenRequire3DSecureModal(false)
      },
      onCloseExpiredCardModal: () => {
        setOpenExpiredCardModal(false)
      },
      onClosePaymentErrorModal: () => {
        setOpenPaymentErrorModal(false)
      },
    },
  }
})

const QUERY = gql`
  query ResaleRaffleRepaymentConfirmation(
    $raffleId: ID!
    $itemId: String!
    $skuId: String
  ) {
    itemResaleRaffleUser(raffleId: $raffleId, itemId: $itemId) {
      ...ItemResaleRaffleUserDetail
    }
    paymentSource {
      ...PaymentDetail
    }
  }
  ${ItemResaleRaffleUserDetailFragmentDoc}
  ${PaymentDetailFragmentDoc}
`

const PURCHASE_RESALE_ITEM = gql`
  mutation purchaseResaleItem($inputs: PurchaseResaleItemInput!) {
    purchaseResaleItem(inputs: $inputs) {
      id
    }
  }
`
